import { Component, OnInit, inject, DestroyRef } from '@angular/core';
import {
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
  ReactiveFormsModule,
} from '@angular/forms';
import { ICountry } from 'src/app/@interfaces/country.interface';
import { ProviderService } from 'src/app/@pages/provider/provider.service';
import { ValidatorsService } from 'src/app/@shared/validator/validators.service';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { IDocumenType } from '../../../../../@interfaces/authentication/documenType';
import { NgxSpinnerService, NgxSpinnerModule } from 'ngx-spinner';
import { TranslateService, TranslatePipe } from '@ngx-translate/core';
import { forkJoin, Observable } from 'rxjs';
import {
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  startWith,
  switchMap,
  take,
} from 'rxjs/operators';
import { IDeclarantTypes } from '../../../../../@interfaces/declarantTypes.interface';
import { IRegimeTypes } from 'src/app/@interfaces/regimeTypes.interface';
import { CustomerService } from '../../../../../@pages/customer/customer.service';
import {
  ICompanyType,
  IStorageCompany,
} from '../../../../../@interfaces/company.interface';
import { IContributorType } from 'src/app/@interfaces/contributorTypes.interface';
import { UsersService } from 'src/app/@pages/users/users.service';
import { CookieAuthService } from 'src/app/@shared/storage-variables/cookie-auth.service';
import { CitiesFormService } from '../../../services/forms/cities-form.service';
import { CitiesFilterService } from '../../../services/filters/cities-filter.service';
import { MatButtonModule } from '@angular/material/button';
import { MatOptionModule } from '@angular/material/core';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { AsyncPipe } from '@angular/common';
import { TributaryService } from 'src/app/@pages/tributary/tributary.service';
import { IActivitiesCiiu } from 'src/app/@interfaces/activitiesciiu.interface';
import { ICities } from 'src/app/@interfaces/cities.interface';
import { AlertService, TableManagerService } from '@shared-services';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ICustomerType } from 'src/app/@interfaces/customerType.interface';

@Component({
  selector: 'app-register-provider',
  templateUrl: './register-provider.component.html',
  styleUrls: ['./register-provider.component.css'],
  imports: [
    MatDialogModule,
    ReactiveFormsModule,
    MatAutocompleteModule,
    MatOptionModule,
    MatButtonModule,
    NgxSpinnerModule,
    AsyncPipe,
    TranslatePipe,
  ],
})
export class RegisterProviderComponent implements OnInit {
  private translate = inject(TranslateService);
  private spinnerService = inject(NgxSpinnerService);
  dialog = inject(MatDialog);
  private fb = inject(UntypedFormBuilder);
  private validatorS = inject(ValidatorsService);
  private providerService = inject(ProviderService);
  private customerService = inject(CustomerService);
  private userService = inject(UsersService);
  private cookieAuthService = inject(CookieAuthService);
  private citiesFormService = inject(CitiesFormService);
  private citiesFilterService = inject(CitiesFilterService);
  private tributaryService = inject(TributaryService);
  private alertService = inject(AlertService);
  private destroyRef = inject(DestroyRef);
  private tableManager = inject(TableManagerService);

  simpleCityList: string[] = [];
  filterCities$: Observable<string[]> = new Observable();
  filterCitiesForColombia$: Observable<ICities[]> = new Observable();
  citiesList: ICities[] = [];
  countryList: Array<ICountry> = [];
  countrySelected: string = '';
  documenTypeList: Array<IDocumenType> = [];
  contributorTypeList: Array<IContributorType> = [];
  declarantsList: Array<IDeclarantTypes> = [];
  regimeList: Array<IRegimeTypes> = [];
  companyTypeList: Array<ICompanyType> = [];
  activitiesList: IActivitiesCiiu[] = []; // CIIU: International Standard Industrial Classification of All Economic Activities
  personTypes: ICustomerType[] = [];

  providerForm: UntypedFormGroup = this.fb.group({
    doc_nit: ['', [Validators.required]],
    verification_digit: [0],
    types_docs: ['', [Validators.required]],
    name: ['', [Validators.required, Validators.maxLength(60)]],
    address: ['', [Validators.required, Validators.maxLength(100)]],
    phone: [
      '',
      [
        Validators.required,
        Validators.pattern(this.validatorS.documentPattern),
        Validators.maxLength(15),
      ],
    ],
    email: [
      '',
      [
        Validators.required,
        this.validatorS.emailValidator,
        Validators.maxLength(50),
      ],
    ],
    country: ['', Validators.required],
    city: ['', Validators.required],
    citySelect: [''],
    contributorType: ['', [Validators.required]],
    personType: ['', Validators.required],
    regime: [''],
    declarant: [''],
    type: [''],
    activity_ciiu: [''],
  });

  companyId = '';
  user = '';
  rol = '';
  companyObject = {} as IStorageCompany;
  countryIsColombia = false;
  providerCountryIsColombia = false;

  ngOnInit() {
    this.getAuthValues();
    this.loadData();
    this.providerForm.get('verification_digit')?.disable();
    if (this.companyObject.countries[0].name === 'Colombia') {
      this.setValid();
    }
    this.onFormChange();
    this.citiesFromStore();

    const countryControl = this.providerForm.get('country')!;
    this.providerForm
      .get('doc_nit')
      ?.addValidators(
        this.validatorS.documentValidatorByCountry(countryControl),
      );
    this.providerForm.get('doc_nit')?.updateValueAndValidity();
  }

  onFormChange() {
    this.providerForm.get('doc_nit')!.valueChanges.subscribe((newValue) => {
      if (newValue) {
        this.providerForm
          .get('verification_digit')
          ?.setValue(
            this.customerService.calculateVerificationDigit(
              newValue.toString(),
            ),
          );
      }
    });
  }

  private loadData() {
    forkJoin({
      countries: this.customerService.listCountries().pipe(take(1)),
      documentTypes: this.userService.listDocumenTypes().pipe(take(1)),
      regimes: this.providerService.getAllRegimeTypes().pipe(take(1)),
      companyTypes: this.providerService.getAllCompanyTypes().pipe(take(1)),
      contributors: this.customerService.listContributorTypes().pipe(take(1)),
    }).subscribe({
      next: (result) => {
        this.countryList = result.countries;
        this.documenTypeList = result.documentTypes;
        this.regimeList = result.regimes;
        this.companyTypeList = result.companyTypes;
        this.contributorTypeList = result.contributors;
      },
    });

    this.customerService
      .listCustomerType()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (types) => (this.personTypes = types),
        error: (error) => console.error('Error loading customer types:', error),
      });
  }

  private citiesFromStore(): void {
    this.citiesFormService.setInitialCitiesFromStore();

    const countryControl = this.providerForm.get('country');
    const cityControl = this.providerForm.get('citySelect');

    countryControl?.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (country) => {
          this.spinnerService.show();
          this.setCitiesList(country);
          this.providerForm
            .get('doc_nit')
            ?.updateValueAndValidity({ emitEvent: false });
        },
      });

    if (cityControl) {
      this.filterCities$ = cityControl.valueChanges.pipe(
        startWith(''),
        map((value) =>
          this.citiesFilterService.filterCities(value, this.simpleCityList),
        ),
      );
      this.filterCitiesForColombia$ = cityControl.valueChanges.pipe(
        startWith(''),
        map((value) =>
          this.citiesFilterService.filterCitiesList(value, this.citiesList),
        ),
      );
    }
  }

  private getAuthValues() {
    this.companyObject = this.cookieAuthService.getCompanyObject!;
    this.companyId = this.companyObject.Id_company!.toString();
    this.user = this.cookieAuthService.getUserId!;
    this.rol = this.cookieAuthService.getRolId!;
    this.countryIsColombia =
      this.companyObject.countries[0].iso === 'COL' ? true : false;
  }

  private setValid() {
    this.providerForm.get('regime')?.setValidators(Validators.required);
    this.providerForm.get('declarant')?.setValidators(Validators.required);
    this.providerForm.get('type')?.setValidators(Validators.required);
    this.providerForm.get('activity_ciiu')?.setValidators(Validators.required);
  }

  register() {
    this.providerForm.markAllAsTouched();
    if (this.providerForm.invalid === true) {
      this.alertService.open({
        title: 'Error',
        message: this.translate.instant(
          'Please, fill in all the required fields!',
        ),
        type: 'error',
        acceptText: 'Ok',
      });
      return;
    } else {
      this.spinnerService.show();
      this.saveOnDataBase();
    }
  }

  private saveOnDataBase() {
    const formValue = this.providerForm.value;

    const toInt = (val: string) => parseInt(val);

    const providerPayload = {
      doc_nit: formValue.doc_nit,
      verification_digit: this.providerForm
        .get('verification_digit')
        ?.getRawValue(),
      id_docs: toInt(formValue.types_docs.id_docs),
      name: formValue.name,
      address: formValue.address,
      phone: formValue.phone,
      email: formValue.email,
      id_country: toInt(formValue.country.id_country),
      city: formValue.city,
      id_contributor: toInt(formValue.contributorType.id_contributor),
      id_person_type: toInt(formValue.personType.id_customerType),
      id_regime: toInt(formValue.regime?.id_regime),
      id_declarant: toInt(formValue.declarant?.id_declarant),
      id_type: toInt(formValue.type?.id_type),
      id_activity: formValue.activity_ciiu?.id_activity,
      user: toInt(this.user!),
    };

    this.providerService
      .registerProvider(
        providerPayload.doc_nit,
        providerPayload.id_docs,
        providerPayload.verification_digit,
        providerPayload.name,
        providerPayload.address,
        providerPayload.phone,
        providerPayload.email,
        providerPayload.id_country,
        providerPayload.city,
        providerPayload.id_contributor,
        providerPayload.id_person_type,
        providerPayload.id_regime,
        providerPayload.id_declarant,
        providerPayload.id_type,
        providerPayload.id_activity,
        providerPayload.user,
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (result) => this.handleRegisterResult(result),
        error: () => this.handleRegisterError(),
      });
  }

  private handleRegisterResult(result: any) {
    this.spinnerService.hide();

    if (result.nombre === 'providerExist') {
      this.alertService.open({
        title: this.translate.instant('Provider was not created'),
        message: this.translate.instant('This provider already exist'),
        type: 'warning',
        acceptText: 'Ok',
      });
      return;
    }

    if (result.nombre !== 'providerExist') {
      this.alertService
        .open({
          title: this.translate.instant('Success'),
          message: this.translate.instant('Provider created successfully'),
          type: 'success',
          acceptText: 'Ok',
        })
        .subscribe({
          accept: () => {
            this.tableManager.refresh();
          },
          close: () => {
            this.tableManager.refresh();
          },
        });
    }
  }

  private handleRegisterError() {
    this.spinnerService.hide();
    this.alertService.open({
      title: 'Error',
      message: this.translate.instant('Something was wrong'),
      type: 'error',
      acceptText: 'Ok',
    });
  }

  textValid(text: string) {
    return (
      this.providerForm.get(text)?.invalid &&
      this.providerForm.get(text)?.touched
    );
  }

  filterDeclarantList() {
    this.providerForm.get('declarant')?.reset();

    this.providerService
      .getDeclarantByRegime(parseInt(this.providerForm.value.regime.id_regime))
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (result) => {
          this.declarantsList = result;
        },
      });
  }

  async setCitiesList(countrySelected: ICountry) {
    const country: ICountry = countrySelected;
    this.providerCountryIsColombia = country.iso === 'COL' ? true : false;
    if (country.name === 'Colombia') {
      this.citiesList = await this.citiesFormService.getOwnCities(
        country.id_country,
      );
      this.spinnerService.hide();
    } else {
      this.simpleCityList = await this.citiesFormService.getCityList(country);
      this.spinnerService.hide();
    }
  }

  setCitySelected() {
    const city: ICities | string = this.providerForm.value.citySelect;
    if (typeof city === 'object') {
      this.providerForm
        .get('city')
        ?.setValue(this.providerForm.value.citySelect.name);
      this.loadActivitiesCiiu(city.id_city);
    } else {
      this.providerForm
        .get('city')
        ?.setValue(this.providerForm.value.citySelect);
      this.loadActivitiesCiiu(null);
    }
  }

  private loadActivitiesCiiu(id_city: number | null) {
    this.providerForm
      .get('activity_ciiu')
      ?.valueChanges.pipe(
        takeUntilDestroyed(this.destroyRef),
        debounceTime(300),
        distinctUntilChanged(),
        filter((value) => typeof value === 'string'),
        switchMap((value: string) =>
          this.tributaryService.searchActivitiesByCity(id_city, value, 0, 15),
        ),
      )
      .subscribe({
        next: (result) => {
          this.activitiesList = result;
        },
      });
  }

  onSelectProvider(city: string) {
    this.providerForm.get('city')?.setValue(city);
  }

  displayFnCity(city: ICities | string): string {
    if (typeof city === 'object') {
      return city.name;
    }
    return city;
  }

  displayFnActivity(activity: IActivitiesCiiu): string {
    return activity && activity.activity_description
      ? activity.activity_description
      : '';
  }
}
